import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import styles from "./PreOrderReviewModal.module.scss";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import CancellationReasonModal from "./CancellationReasonModal";

const buttonSx = {
  width: "auto !important",
  minWidth: "unset !important",
  height: "auto",
  padding: "10px 20px",
  whiteSpace: "nowrap",
};

const PreOrderReviewModal = ({
  open,
  onClose,
  onAccept,
  onCancelOrder,
  onCancelSuccess,
  priorityLevel,
  orderId,
}) => {
  const { t } = useTranslation();

  const [isAccepted, setIsAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCancellationOpen, setIsCancellationOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!open) {
      setErrorMessage("");
      setIsAccepted(false);
      setIsSubmitting(false);
      setIsCancellationOpen(false);
    }
  }, [open]);

  const handleDialogClose = (_event, reason) => {
    if (reason === "backdropClick" || reason === undefined) {
      onClose();
    }
  };

  const getErrorMessage = (error) => {
    const status = error?.response?.status;
    if (status === 404) {
      return t("CustomerOrderDetails.preOrder.modal.error.recordNotFound");
    }
    if (status === 422) {
      return t(
        "CustomerOrderDetails.preOrder.modal.error.recordCannotBeUpdated"
      );
    }
    return t("CustomerOrderDetails.preOrder.modal.error.tryAgain");
  };

  const handleAccept = async () => {
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      await onAccept();
      setIsAccepted(true);
    } catch (error) {
      console.error("Failed to accept pre-order:", error);
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenCancellation = () => {
    setIsCancellationOpen(true);
  };

  const handleGoBackToReview = () => {
    setIsCancellationOpen(false);
  };

  const handleCloseCancellation = () => {
    setIsCancellationOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open && !isCancellationOpen}
        onClose={handleDialogClose}
        maxWidth={false}
        data-testid="preorder-review-modal"
        PaperProps={{
          className: styles.modalPaper,
        }}
      >
        <IconButton
          onClick={onClose}
          className={styles.closeButton}
          data-testid="preorder-modal-close"
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {isAccepted ? (
          <>
            <DialogTitle className={styles.modalTitleContainer}>
              <Typography className={styles.modalTitle}>
                {t("CustomerOrderDetails.preOrder.modal.acceptedTitle")}
              </Typography>
            </DialogTitle>

            <DialogContent className={styles.modalContent}>
              <Typography className={styles.descriptionText}>
                {t("CustomerOrderDetails.preOrder.modal.acceptedBody", {
                  priorityLevel,
                })}
              </Typography>
            </DialogContent>

            <DialogActions className={styles.modalActions}>
              <EMPButton
                variant="contained"
                onClick={onClose}
                isFord
                testId="preorder-modal-close-success"
                sx={buttonSx}
              >
                {t("CustomerOrderDetails.preOrder.modal.closeButton")}
              </EMPButton>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle className={styles.modalTitleContainer}>
              <Typography className={styles.modalTitle}>
                {t("CustomerOrderDetails.preOrder.modal.title")}
              </Typography>
            </DialogTitle>

            <DialogContent className={styles.modalContent}>
              <Typography className={styles.descriptionText}>
                {t("CustomerOrderDetails.preOrder.modal.acceptBody", {
                  priorityLevel,
                })}
              </Typography>

              <Typography className={styles.descriptionText}>
                {t("PreOrderReviewModalCopy.cancelBodyBefore")}{" "}
                <Box component="span">
                  {t("PreOrderReviewModalCopy.cancelBodyNew")}
                </Box>{" "}
                {t("PreOrderReviewModalCopy.cancelBodyAfter")}
              </Typography>

              <Typography className={styles.disclaimerText}>
                {t("CustomerOrderDetails.preOrder.modal.disclaimer")}
              </Typography>

              {errorMessage && (
                <Typography
                  role="alert"
                  color="error"
                  data-testid="preorder-modal-error"
                >
                  {errorMessage}
                </Typography>
              )}
            </DialogContent>

            <DialogActions className={styles.modalActions}>
              <EMPButton
                variant="outlined"
                onClick={handleOpenCancellation}
                disabled={isSubmitting}
                isFord
                testId="preorder-modal-cancel"
                sx={buttonSx}
              >
                {t("CustomerOrderDetails.preOrder.modal.cancelPreOrderButton")}
              </EMPButton>

              <EMPButton
                variant="contained"
                onClick={handleAccept}
                disabled={isSubmitting}
                isFord
                testId="preorder-modal-accept"
                sx={buttonSx}
              >
                {t("CustomerOrderDetails.preOrder.modal.acceptPreOrderButton")}
              </EMPButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      <CancellationReasonModal
        open={open && isCancellationOpen}
        orderId={orderId}
        onClose={handleCloseCancellation}
        onGoBack={handleGoBackToReview}
        onCancelOrder={onCancelOrder}
        onCancelSuccess={onCancelSuccess}
      />
    </>
  );
};

PreOrderReviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAccept: PropTypes.func.isRequired,
  onCancelOrder: PropTypes.func.isRequired,
  onCancelSuccess: PropTypes.func.isRequired,
  priorityLevel: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default PreOrderReviewModal;
